import {onError} from '@apollo/client/link/error';

import notifier from '@albert-io/notifier';

/**
 * Keys to strip from GraphQL variables before sending to Honeybadger,
 * as a safeguard against PII leaking into error reports.
 * Aligned with the CONTENT_FILTERS list in the Honeybadger browser config,
 * plus camelCase variants used in GraphQL variable conventions.
 */
const SENSITIVE_VARIABLE_KEYS = new Set([
  'token',
  'email',
  'username',
  'password',
  'oldPassword',
  'newPassword',
  'verify_old_password',
  'verifyOldPassword',
  'password_reset_link',
  'passwordResetLink',
  'verify_password_reset_link',
  'verifyPasswordResetLink',
  'stripe_customer_id',
  'stripeCustomerId',
  'first_name',
  'firstName',
  'last_name',
  'lastName',
  'salutation',
  'consent_email',
  'consentEmail'
]);

/**
 * Recursively strips known-sensitive keys from a value.
 * Handles nested objects and arrays at any depth.
 */
function sanitizeValue(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map((item) => sanitizeValue(item));
  }

  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([key, val]) =>
        SENSITIVE_VARIABLE_KEYS.has(key) ? [key, '[FILTERED]'] : [key, sanitizeValue(val)]
      )
    );
  }

  return value;
}

/**
 * Recursively strips known-sensitive keys from a variables object.
 * Returns a deep copy with sensitive values replaced by '[FILTERED]'.
 */
export function sanitizeVariables(
  variables: Record<string, unknown> | undefined
): Record<string, unknown> | undefined {
  if (!variables) return undefined;

  return sanitizeValue(variables) as Record<string, unknown>;
}

/**
 * Apollo error link that reports GraphQL response errors to Honeybadger.
 *
 * Only reports errors from the GraphQL `errors` response array (application-level
 * errors). Network errors (connection failures, timeouts, HTTP status errors) are
 * intentionally excluded — they are transient, not actionable from the frontend,
 * and are already monitored by backend logging.
 *
 * - Tagged `graphql-error`
 * - Component: `apollo-graphql`
 * - Fingerprinted by operation name for easy grouping
 *
 * This link never swallows errors — they still propagate to components.
 */
// TODO(AC4): `graphQLErrors` is deprecated in @apollo/client 3.14+ and will be removed
// in Apollo Client 4.0. It is consolidated into a single `error` property. When upgrading
// to AC4, migrate this callback to use `error` instead.
// See: https://www.apollographql.com/docs/react/migrating/apollo-client-4
const errorLink = onError(({graphQLErrors, operation}) => {
  if (!graphQLErrors) {
    return;
  }

  const operationName = operation.operationName || 'UnknownOperation';
  const operationDefinition = operation.query.definitions.find(
    (def) => def.kind === 'OperationDefinition'
  );
  const operationType =
    operationDefinition?.kind === 'OperationDefinition' ? operationDefinition.operation : 'unknown';

  graphQLErrors.forEach((gqlError) => {
    notifier.notify(new Error(gqlError.message), {
      message: `GraphQL error in ${operationName}: ${gqlError.message}`,
      tags: ['graphql-error'],
      component: 'apollo-graphql',
      fingerprint: `graphql-error-${operationName}`,
      context: {
        operationName,
        operationType,
        errorMessage: gqlError.message,
        errorPath: gqlError.path,
        errorExtensions: gqlError.extensions,
        variables: sanitizeVariables(operation.variables)
      }
    });
  });
});

export default errorLink;
